import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, Loader2, Send, CheckCircle, AlertCircle,
  User, Briefcase, Brain, MessageSquare, Zap
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const getCategoryIcon = (category) => {
  const cat = category?.toLowerCase() || '';
  if (cat.includes('cognitif') || cat.includes('logique')) return <Brain className="h-5 w-5 text-blue-500" />;
  if (cat.includes('social') || cat.includes('contenu')) return <MessageSquare className="h-5 w-5 text-purple-500" />;
  if (cat.includes('mental') || cat.includes('resolution')) return <Zap className="h-5 w-5 text-yellow-500" />;
  if (cat.includes('execution')) return <Briefcase className="h-5 w-5 text-green-500" />;
  if (cat.includes('technique') || cat.includes('business')) return <Brain className="h-5 w-5 text-indigo-500" />;
  return <User className="h-5 w-5 text-gray-500" />;
};

const getCategoryLabel = (category) => {
  const labels = {
    'cognitif': 'Cognitif',
    'logique': 'Logique & Priorisation',
    'social': 'Social / Communication',
    'communication': 'Communication Client',
    'mental': 'Mental / Discipline',
    'execution': 'Exécution',
    'resolution': 'Résolution de problèmes',
    'technique': 'Technique',
    'business': 'Business & ROI',
    'contenu': 'Stratégie Contenu'
  };
  return labels[category?.toLowerCase()] || category;
};

const QualificationFormPage = () => {
  const { candidateId, profileType } = useParams();
  const navigate = useNavigate();
  
  const [candidate, setCandidate] = useState(null);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, profileType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Récupérer le candidat
      const candidateRes = await api.get(`/admin/candidates/${candidateId}`);
      if (candidateRes.ok) {
        const candidateData = await candidateRes.json();
        setCandidate(candidateData);
      } else {
        toast.error('Candidat non trouvé');
        navigate('/admin/candidates');
        return;
      }

      // Récupérer le formulaire
      const formRes = await api.get(`/qualification/forms/${profileType}`);
      if (formRes.ok) {
        const formData = await formRes.json();
        
        // Flatten questions from sections if needed
        let questions = formData.questions || [];
        if (formData.sections && Array.isArray(formData.sections)) {
          questions = formData.sections.flatMap(section => 
            (section.questions || []).map(q => ({
              ...q,
              category: section.category || section.id
            }))
          );
        }
        
        setForm({
          ...formData,
          questions
        });
        
        // Initialiser les réponses
        const initialAnswers = {};
        questions.forEach(q => {
          initialAnswers[q.id] = '';
        });
        setAnswers(initialAnswers);
      } else {
        toast.error('Formulaire non trouvé');
        navigate('/admin/candidates');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async () => {
    // Vérifier que toutes les questions sont répondues
    const unanswered = form.questions.filter(q => !answers[q.id]?.trim());
    if (unanswered.length > 0) {
      toast.error(`${unanswered.length} question(s) sans réponse`);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        candidate_id: candidateId,
        profile_type: profileType,
        answers: Object.entries(answers).map(([question_id, answer]) => ({
          question_id,
          answer
        }))
      };

      const response = await api.post('/qualification/submit', payload);
      
      if (response.ok) {
        const result = await response.json();
        toast.success('Qualification soumise avec succès');
        
        // Rediriger vers le dashboard des qualifications
        navigate('/admin/qualifications');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Erreur soumission:', error);
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  // Grouper les questions par catégorie
  const getQuestionsByCategory = () => {
    if (!form) return {};
    
    const grouped = {};
    form.questions.forEach(q => {
      if (!grouped[q.category]) {
        grouped[q.category] = [];
      }
      grouped[q.category].push(q);
    });
    return grouped;
  };

  const categories = form ? Object.keys(getQuestionsByCategory()) : [];
  const questionsByCategory = getQuestionsByCategory();

  // Calculer la progression
  const totalQuestions = form?.questions?.length || 0;
  const answeredQuestions = Object.values(answers).filter(a => a?.trim()).length;
  const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-tp-primary" />
          <span className="ml-3 text-tp-text-light">Chargement du formulaire...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="qualification-form-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour
        </Button>
      </div>

      {/* Candidate Info */}
      <Card className="p-6 mb-6 border-tp-border bg-tp-primary/5">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-tp-primary/10 rounded-full">
            <User className="h-6 w-6 text-tp-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-tp-text">
              {candidate?.first_name} {candidate?.last_name}
            </h2>
            <p className="text-sm text-tp-text-light">{candidate?.email}</p>
          </div>
        </div>
      </Card>

      {/* Form Header */}
      <Card className="p-6 mb-6 border-tp-border">
        <h1 className="text-2xl font-display font-semibold text-tp-text mb-2">
          {form?.title}
        </h1>
        <p className="text-tp-text-light mb-4">{form?.description}</p>
        
        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-tp-text-light">Progression</span>
            <span className="font-medium text-tp-text">{answeredQuestions}/{totalQuestions} questions</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </Card>

      {/* Questions by Category */}
      {categories.map((category, categoryIndex) => (
        <Card key={category} className="p-6 mb-6 border-tp-border">
          <div className="flex items-center gap-3 mb-6">
            {getCategoryIcon(category)}
            <div>
              <h3 className="text-lg font-semibold text-tp-text">
                {getCategoryLabel(category)}
              </h3>
              <p className="text-sm text-tp-text-light">
                {questionsByCategory[category].length} question(s)
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {questionsByCategory[category].map((question, qIndex) => (
              <div key={question.id} className="space-y-2">
                <Label className="text-tp-text font-medium">
                  {qIndex + 1}. {question.question}
                </Label>
                <Textarea
                  value={answers[question.id] || ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  placeholder={question.placeholder || "Votre réponse..."}
                  rows={4}
                  className="resize-none"
                  data-testid={`answer-${question.id}`}
                />
                {answers[question.id]?.trim() && (
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Répondu
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}

      {/* Submit */}
      <Card className="p-6 border-tp-border">
        <div className="flex items-center justify-between">
          <div>
            {answeredQuestions < totalQuestions && (
              <p className="text-sm text-orange-600 flex items-center gap-1">
                <AlertCircle className="h-4 w-4" />
                {totalQuestions - answeredQuestions} question(s) sans réponse
              </p>
            )}
            {answeredQuestions === totalQuestions && (
              <p className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Toutes les questions sont répondues
              </p>
            )}
          </div>
          
          <Button
            onClick={handleSubmit}
            disabled={submitting || answeredQuestions < totalQuestions}
            className="bg-tp-primary hover:bg-tp-primary-light"
            data-testid="submit-qualification-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyse en cours...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Soumettre pour analyse
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default QualificationFormPage;
